import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Pencil, History } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  fetchBoxPrices, fetchBoxPriceHistory, updateBoxPrice,
  type BoxPrice, type BoxPriceChange,
} from "@/lib/boxControlApi";
import { formatDateTimeBrasilia, formatMoney, materialTypeLabel } from "@/lib/boxLabels";

export default function AdminBoxPrices() {
  const [prices, setPrices] = useState<BoxPrice[]>([]);
  const [history, setHistory] = useState<BoxPriceChange[]>([]);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  const [editing, setEditing] = useState<BoxPrice | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, h] = await Promise.all([fetchBoxPrices(), fetchBoxPriceHistory()]);
      setPrices(p);
      setHistory(h);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Falha ao carregar preços");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  return (
    <div className="container py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Preços de caixas</h1>
          <p className="text-sm text-muted-foreground">
            Preços de referência usados no cálculo proporcional da venda. Alterações mantêm histórico.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setShowHistory((v) => !v)}>
          <History className="mr-1 h-4 w-4" /> {showHistory ? "Ocultar histórico" : "Histórico"}
        </Button>
      </div>

      <div className="rounded-lg border border-border">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Carregando…
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Material/Tipo</TableHead>
                <TableHead className="text-right">Qtd de referência</TableHead>
                <TableHead className="text-right">Preço</TableHead>
                <TableHead>Situação</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {prices.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>
                    {materialTypeLabel(p.materialName, p.typeName)}
                    {(!p.materialActive || !p.typeActive) && (
                      <span className="ml-2 text-xs text-muted-foreground">(inativo)</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">{p.referenceQuantity ?? "—"}</TableCell>
                  <TableCell className="text-right">{p.priced ? formatMoney(p.referencePrice) : "—"}</TableCell>
                  <TableCell>
                    <Badge variant={p.priced ? "secondary" : "outline"}>
                      {p.priced ? "Cadastrado" : "Não cadastrado"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => setEditing(p)}>
                      <Pencil className="mr-1 h-4 w-4" /> Editar
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {!loading && prices.some((p) => !p.priced) && (
        <p className="text-sm text-muted-foreground">
          Materiais sem preço cadastrado não podem ser vendidos — a venda fica bloqueada até o cadastro
          (o preço ausente nunca é tratado como zero). Empréstimos continuam liberados.
        </p>
      )}

      {showHistory && (
        <div className="rounded-lg border border-border p-4">
          <div className="mb-2 text-sm font-medium">Histórico de alterações</div>
          {history.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhuma alteração registrada.</p>
          ) : (
            <div className="space-y-1 text-sm">
              {history.map((h, idx) => (
                <div key={idx} className="rounded-md border border-border px-3 py-1.5">
                  <span className="font-medium">{materialTypeLabel(h.materialName, h.typeName)}</span>
                  {": "}
                  {h.oldReferencePrice === null ? "— " : `${h.oldReferenceQuantity}/${formatMoney(h.oldReferencePrice)} `}
                  →{" "}
                  {`${h.newReferenceQuantity}/${formatMoney(h.newReferencePrice)}`}
                  <span className="text-muted-foreground">
                    {" "}· {formatDateTimeBrasilia(h.changedAt)} · {h.actorEmail}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {editing && (
        <EditPriceDialog
          price={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); void load(); }}
        />
      )}
    </div>
  );
}

function EditPriceDialog({
  price, onClose, onSaved,
}: { price: BoxPrice; onClose: () => void; onSaved: () => void }) {
  const [quantity, setQuantity] = useState(price.referenceQuantity ? String(price.referenceQuantity) : "");
  const [value, setValue] = useState(price.referencePrice != null ? String(price.referencePrice) : "");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const qty = Number.parseInt(quantity, 10);
    const amount = Number.parseFloat(value.replace(",", "."));
    if (Number.isNaN(qty) || qty < 1) { toast.error("Quantidade de referência deve ser ≥ 1"); return; }
    if (Number.isNaN(amount) || amount < 0) { toast.error("Preço inválido"); return; }
    setSaving(true);
    try {
      await updateBoxPrice(price.id, { referenceQuantity: qty, referencePrice: amount });
      toast.success("Preço atualizado");
      onSaved();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Falha ao atualizar preço");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Editar preço — {materialTypeLabel(price.materialName, price.typeName)}</DialogTitle>
          <DialogDescription>
            O cálculo da venda é proporcional: valor = quantidade × preço ÷ quantidade de referência.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Quantidade de referência</Label>
            <Input type="number" min={1} step={1} value={quantity} onChange={(e) => setQuantity(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Preço (R$)</Label>
            <Input inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} placeholder="0,00" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={() => void submit()} disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
